import { parsePageRange } from './pdfOperations';

/**
 * Sanitize a 1-based page list against the document size:
 * dedupe, sort, clamp to [1, totalPages].
 */
function sanitizePages(pages, totalPages) {
  if (!Array.isArray(pages)) return [];
  const out = new Set();
  for (const p of pages) {
    const n = Number(p);
    if (Number.isInteger(n) && n >= 1 && n <= totalPages) out.add(n);
  }
  return Array.from(out).sort((a, b) => a - b);
}

/**
 * Plan a delete_pages execution. Returns either
 *   { keepNums: number[], summary: { kind: 'delete', pages: number[] } }
 * or
 *   { error: 'no_pages' | 'delete_all' }
 *
 * keepNums is what we pass to splitPDF: every page NOT in action.pages.
 * summary is the source for buildUndoSummary().
 */
export function planDeleteExecution(action, totalPages) {
  const targetPages = sanitizePages(action?.pages, totalPages);
  if (targetPages.length === 0) return { error: 'no_pages' };
  if (targetPages.length >= totalPages) return { error: 'delete_all' };

  const toDelete = new Set(targetPages);
  const keepNums = [];
  for (let p = 1; p <= totalPages; p++) {
    if (!toDelete.has(p)) keepNums.push(p);
  }
  return { keepNums, summary: { kind: 'delete', pages: targetPages } };
}

/**
 * Plan an extract_pages execution. Returns either
 *   { keepNums: number[], summary: { kind: 'extract', pages: number[] } }
 * or
 *   { error: 'no_pages' }
 *
 * keepNums = the sanitized pages themselves (splitPDF keeps these in order).
 */
export function planExtractExecution(action, totalPages) {
  const targetPages = sanitizePages(action?.pages, totalPages);
  if (targetPages.length === 0) return { error: 'no_pages' };
  return { keepNums: targetPages, summary: { kind: 'extract', pages: targetPages } };
}

/**
 * Parse a tolerant comma-separated group plan into 1-based page groups.
 * Example: "1-3, 4-5, 6-10" → [[1,2,3],[4,5],[6,7,8,9,10]]
 *
 * Each comma-separated token is treated as ONE GROUP and parsed via
 * parsePageRange. Groups that resolve to zero in-range pages are dropped.
 * Returns null if nothing usable can be parsed.
 */
export function parseAiGroups(target, totalPages) {
  if (typeof target !== 'string') return null;
  const trimmed = target.trim();
  if (!trimmed) return null;

  const groups = [];
  for (const token of trimmed.split(',')) {
    const t = token.trim();
    if (!t) continue;
    // `t` is a single token (no commas); parsePageRange's own comma-split is a no-op here.
    const pages = parsePageRange(t, totalPages);
    if (pages.length > 0) groups.push(pages);
  }
  return groups.length > 0 ? groups : null;
}

/**
 * Normalize a compression level target from the AI.
 * Returns 'low' | 'medium' | 'high' or null.
 */
export function normalizeAiLevel(target) {
  if (typeof target !== 'string') return null;
  const t = target.trim().toLowerCase();
  if (t === 'low' || t === 'medium' || t === 'high') return t;
  return null;
}

/**
 * Normalize a convert_to_image format target. Maps "jpeg" → "jpg".
 * Returns 'png' | 'jpg' or null.
 */
export function normalizeAiFormat(target) {
  if (typeof target !== 'string') return null;
  const t = target.trim().toLowerCase();
  if (t === 'png') return 'png';
  if (t === 'jpg' || t === 'jpeg') return 'jpg';
  return null;
}

/**
 * Build a structured undo-toast summary from a planDeleteExecution /
 * planExtractExecution summary. Returns { key, args } the caller passes
 * to i18next's t().
 */
export function buildUndoSummary(summary) {
  const pages = summary.pages;
  const pagesStr = pages.join(', ');
  if (summary.kind === 'delete') {
    if (pages.length === 1) return { key: 'ai.undo_toast_delete_one', args: { pages: pagesStr } };
    return { key: 'ai.undo_toast_delete_many', args: { count: pages.length, pages: pagesStr } };
  }
  if (summary.kind === 'extract') {
    if (pages.length === 1) return { key: 'ai.undo_toast_extract_one', args: { pages: pagesStr } };
    return { key: 'ai.undo_toast_extract_many', args: { count: pages.length, pages: pagesStr } };
  }
  return { key: 'ai.undo_toast_generic', args: {} };
}
